import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WhatsAppIcon } from "@/components/Icon";
import { EMAIL, PHONES, ADDRESS, WA_DEFAULT, TAGLINE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { INDUSTRY_PAGES } from "@/data/industries";

const Col = ({ title, children }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E8B795]">{title}</p>
    <ul className="mt-5 space-y-3 text-[14px] text-[#CFCBC0]">{children}</ul>
  </div>
);

const FLink = ({ to, children }) => (
  <li><Link to={to} className="transition-colors hover:text-white">{children}</Link></li>
);

export const Footer = () => (
  <footer className="relative overflow-hidden bg-charcoal text-ivory" data-testid="site-footer">
    <div className="grid-lines-dark absolute inset-0 opacity-60" aria-hidden="true" />
    <div className="wrap relative pt-20">
      <div className="grid gap-12 border-b border-white/10 pb-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.3fr]">
        <div>
          <Logo light full className="h-16 sm:h-20" />
          <p className="mt-6 max-w-sm text-[14px] leading-relaxed text-[#CFCBC0]">
            Digital solutions, intelligent software, and connected business systems for modern industry. Websites, ERP, CRM, mobile apps, automation and SEO for manufacturers worldwide.
          </p>
        </div>
        <Col title="Company">
          <FLink to="/about">About Us</FLink>
          <FLink to="/solutions">Solutions</FLink>
          <FLink to="/process">Our Process</FLink>
          <FLink to="/industries">Industries</FLink>
          <FLink to="/portfolio">Portfolio</FLink>
          <FLink to="/contact">Contact</FLink>
          <FLink to="/sitemap">Sitemap</FLink>
        </Col>
        <Col title="Services">
          {SERVICES.slice(0, 7).map((s) => <FLink key={s.slug} to={`/services/${s.slug}`}>{s.title}</FLink>)}
          <FLink to="/services">All services</FLink>
        </Col>
        <Col title="Industries">
          {INDUSTRY_PAGES.slice(0, 7).map((i) => <FLink key={i.slug} to={`/industries/${i.slug}`}>{i.name}</FLink>)}
          <FLink to="/industries">All industries</FLink>
        </Col>
        <Col title="Contact">
          <li><a href={`mailto:${EMAIL}`} className="flex items-start gap-3 hover:text-white" data-testid="footer-email-link"><Mail className="mt-0.5 h-4 w-4 shrink-0" />{EMAIL}</a></li>
          {PHONES.map((p) => (
            <li key={p.href}><a href={p.href} className="flex items-center gap-3 hover:text-white" data-testid={`footer-phone-${p.href.slice(-4)}`}><Phone className="h-4 w-4 shrink-0" />{p.label}</a></li>
          ))}
          <li><a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white" data-testid="footer-whatsapp-link"><WhatsAppIcon className="h-4 w-4 shrink-0" />WhatsApp us</a></li>
          <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0" /><address className="not-italic leading-relaxed">{ADDRESS.full}</address></li>
        </Col>
      </div>
      <p className="select-none py-10 text-center font-display text-[13vw] font-extrabold leading-none tracking-tight text-white/[0.05] lg:text-[10rem]" style={{ fontStretch: "125%" }} aria-hidden="true">INDUSTRIX</p>
      <div className="flex flex-col gap-4 border-t border-white/10 py-8 text-[13px] text-[#BDB8AC] md:flex-row md:items-center md:justify-between">
        <p data-testid="footer-copyright">© {new Date().getFullYear()} Industrix Solution. All rights reserved. · {TAGLINE}</p>
        <div className="flex flex-wrap gap-6">
          <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white" data-testid="footer-terms-link">Terms & Conditions</Link>
          <Link to="/sitemap" className="hover:text-white">Sitemap</Link>
        </div>
      </div>
    </div>
  </footer>
);
